import { IconButton, MenuItem, MenuList, Popover, Stack, Typography } from '@mui/material';
import { DropdownSelectRow } from '@schooly/components/filters';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CrossIcon } from '../../assets/icons';
import { Spin } from '../Spin';
import { CellProps } from './cell.type';
import { ColorCellWrapper } from './CellWrappers';

const CELL_DEFAULT_WIDTH = 112;

export type DropdownCellProps<T> = CellProps<T> & {
  isDisabled?: boolean;
  options: T[];
  showCellBorder?: boolean;
  anchorOffset?: number;
  dropdownStyleProps?: React.CSSProperties;
};

export const DropdownCell = <T extends { name: string; id: string }>({
  isDisabled,
  value,
  options,
  canEdit = true,
  status,
  backgroundColor,
  textColor,
  canReset,
  onUpdate,
  width = CELL_DEFAULT_WIDTH,
  focused,
  height,
  anchorOffset,
  dropdownStyleProps,
}: DropdownCellProps<T>) => {
  const cellRef = useRef<HTMLDivElement>(null);
  const { anchorEl, isOpen, opensDownward, handleOpen, handleClose } = useDropdownState(
    focused,
    cellRef,
  );

  const isLoading = status === 'loading';
  const isError = status === 'error';
  const displayValue = value?.name ?? '-';
  const colors = useDropdownCellColors(status, value?.name ?? null, textColor);

  const handleOptionClick = useCallback(
    async (option: T) => {
      handleClose();
      if (option.id === value?.id) return;
      try {
        await onUpdate(option);
      } catch (error) {
        console.error(error);
      }
    },
    [value?.id, onUpdate, handleClose],
  );

  const handleReset = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onUpdate(null);
      handleClose();
    },
    [onUpdate, handleClose],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (isOpen) {
        e.stopPropagation();
        return;
      }
      if (canEdit && !isLoading) {
        handleOpen(e);
      }
    },
    [canEdit, isLoading, handleOpen, isOpen],
  );

  const renderCellContent = useCallback(() => {
    return (
      <Stack
        sx={(theme) => ({
          height,
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          borderBottom: opensDownward ? theme.mixins.borderControlValue() : 'none',
          borderTop: !opensDownward ? theme.mixins.borderControlValue() : 'none',
        })}
      >
        <Typography
          variant="h3"
          sx={{
            color: 'primary.main',
            textAlign: 'center',
          }}
        >
          {displayValue}
        </Typography>
        {canReset && value && isOpen && (
          <IconButton
            size="small"
            onClick={handleReset}
            sx={{
              position: 'absolute',
              right: 8,
            }}
            inverse
          >
            <CrossIcon />
          </IconButton>
        )}
      </Stack>
    );
  }, [canReset, displayValue, handleReset, height, isOpen, opensDownward, value]);
  return (
    <>
      <Stack
        ref={cellRef}
        onClick={handleClick}
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          width,
          height: height ?? '100%',
          pointerEvents: isDisabled ? 'none' : 'auto',
          position: 'relative',
          backgroundColor: isError ? 'error.superLight' : undefined,
          cursor: canEdit && !isLoading ? 'pointer' : 'default',
        }}
      >
        {!isLoading && !isOpen && (
          <ColorCellWrapper color={backgroundColor ?? null} error={isError} />
        )}
        {isLoading && <Spin size={2} />}
        {!isLoading && !isOpen && (
          <Typography
            variant="h3"
            sx={{
              color: isDisabled ? 'common.grey6' : colors.valueColor,
              textAlign: 'center',
              userSelect: 'none',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {displayValue}
          </Typography>
        )}
        {!isLoading && isOpen && renderCellContent()}
      </Stack>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: opensDownward ? 'top' : 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: opensDownward ? 'top' : 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              width: width ?? 'auto',
              ...theme.mixins.borderControl(),
              boxShadow: opensDownward
                ? '0px 8px 20px -4px rgba(0, 0, 0, 0.15)'
                : '0px -8px 20px -4px rgba(0, 0, 0, 0.15)',
              borderTopLeftRadius: opensDownward ? 0 : undefined,
              borderTopRightRadius: opensDownward ? 0 : undefined,
              borderBottomLeftRadius: !opensDownward ? 0 : undefined,
              borderBottomRightRadius: !opensDownward ? 0 : undefined,
              ...(anchorOffset && !opensDownward
                ? { transform: `translateY(${anchorOffset}px) !important` }
                : {}),
              ...(dropdownStyleProps ? dropdownStyleProps : {}),
            }),
          },
        }}
        disableEnforceFocus
        disableAutoFocus
      >
        <Stack
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {opensDownward && renderCellContent()}
          <MenuList
            autoFocusItem
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                handleClose();
              }
            }}
            sx={(theme) => ({
              padding: theme.spacing(1, 0.5),
              '& .MuiMenuItem-root': {
                padding: 0,
                minHeight: 'unset',
              },
            })}
          >
            {options.map((option) => (
              <MenuItem
                key={option.id}
                onClick={() => handleOptionClick(option)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleOptionClick(option);
                  }
                }}
                sx={(theme) => ({
                  padding: 0,
                  '&:hover, &:focus, &.Mui-focusVisible': {
                    backgroundColor: theme.palette.background.default,
                    borderRadius: theme.spacing(0.5),
                  },
                  '&:last-child': {
                    marginBottom: 0,
                  },
                })}
              >
                <DropdownSelectRow isSelected={option.id === value?.id} sx={{ width: '100%' }}>
                  {option.name}
                </DropdownSelectRow>
              </MenuItem>
            ))}
          </MenuList>
          {!opensDownward ? renderCellContent() : null}
        </Stack>
      </Popover>
    </>
  );
};

const useDropdownCellColors = (
  status: DropdownCellProps<any>['status'],
  value: string | null,
  textColor?: [string, string | undefined],
) => {
  const isError = status === 'error';
  const [valueColor] = textColor || [];

  return useMemo(() => {
    if (isError) {
      return {
        valueColor: 'error.main',
        backgroundColor: 'error.superLight',
      };
    }

    if (!value) {
      return {
        valueColor: 'common.grey',
      };
    }

    return {
      valueColor: valueColor || 'primary.main',
    };
  }, [isError, value, valueColor]);
};

const useDropdownState = (
  focused: boolean | undefined,
  cellRef: React.RefObject<HTMLDivElement>,
) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [opensDownward, setOpensDownward] = useState(true);
  const prevFocusedRef = useRef(focused);
  const isOpen = Boolean(anchorEl);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpensDownward(spaceBelow > spaceAbove);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    const wasFocused = prevFocusedRef.current;
    const becameFocused = focused && !wasFocused;

    prevFocusedRef.current = focused;

    if (becameFocused && cellRef.current) {
      cellRef.current.click();
    }
  }, [focused, cellRef]);

  return {
    anchorEl,
    isOpen,
    opensDownward,
    handleOpen,
    handleClose,
  };
};
